import { createSlice } from "@reduxjs/toolkit";

import type { IGameState, IPlayer, TTeamSides } from "../../types";

export type CurrentHitter = IPlayer & {
  position: number;
};

export const initialState = {
  isGameActive: false,
  areRostersOpen: true,
  gameState: {
    paOfInning: 1,
    pitchOfPa: 1,
    awayScore: 0,
    homeScore: 0,
    inning: {
      integer: 1,
      half: "top",
    },
    count: {
      balls: 0,
      strikes: 0,
    },
    outs: 0,
  } as IGameState,
  rosters: {
    away: {
      name: "",
      uuid: "",
      roster: [] as Array<IPlayer>,
      currentLineup: [] as Array<IPlayer>,
      currentHitter: {} as CurrentHitter,
      currentPitcher: {} as IPlayer,
      currentCatcher: {} as IPlayer,
      pitchCount: 0 as number,
    },
    home: {
      name: "",
      uuid: "",
      roster: [] as Array<IPlayer>,
      currentLineup: [] as Array<IPlayer>,
      currentHitter: {} as CurrentHitter,
      currentPitcher: {} as IPlayer,
      currentCatcher: {} as IPlayer,
      pitchCount: 0 as number,
    },
  },
};

const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    endGame: (state) => {
      state = {
        isGameActive: false,
        areRostersOpen: true,
        gameState: {
          paOfInning: 1,
          pitchOfPa: 1,
          awayScore: 0,
          homeScore: 0,
          inning: {
            integer: 1,
            half: "top",
          },
          count: {
            balls: 0,
            strikes: 0,
          },
          outs: 0,
        },
        rosters: {
          away: {
            name: "",
            uuid: "",
            roster: [],
            currentLineup: [],
            currentHitter: {} as CurrentHitter,
            currentPitcher: {} as IPlayer,
            currentCatcher: {} as IPlayer,
            pitchCount: 0,
          },
          home: {
            name: "",
            uuid: "",
            roster: [],
            currentLineup: [],
            currentHitter: {} as CurrentHitter,
            currentPitcher: {} as IPlayer,
            currentCatcher: {} as IPlayer,
            pitchCount: 0,
          },
        },
      };
      return state;
    },
    clearRoster: (state, action: { payload: { team: TTeamSides } }) => {
      state.rosters[action.payload.team].name = "";
      state.rosters[action.payload.team].uuid = "";
      state.rosters[action.payload.team].roster = [];
      state.rosters[action.payload.team].currentLineup = [];
      state.rosters[action.payload.team].currentPitcher = {} as IPlayer;
      state.rosters[action.payload.team].currentHitter = {} as CurrentHitter;
      state.rosters[action.payload.team].currentCatcher = {} as IPlayer;
    },
    setGameActive: (state, action) => {
      state.isGameActive = action.payload;
    },
    setGameState: (state, action) => {
      state.gameState = action.payload;
    },
    setAreRostersOpen: (state, action) => {
      state.areRostersOpen = action.payload;
    },
    setRoster: (
      state,
      action: { payload: { team: TTeamSides; roster: Array<IPlayer> } },
    ) => {
      state.rosters[action.payload.team].roster = action.payload.roster;
    },
    setTeam: (
      state,
      action: { payload: { team: TTeamSides; name: string; uuid: string } },
    ) => {
      state.rosters[action.payload.team].name = action.payload.name;
      state.rosters[action.payload.team].uuid = action.payload.uuid;
    },
    makeOffensiveSub: (
      state,
      action: {
        payload: { team: TTeamSides; index: number; newPlayer: IPlayer };
      },
    ) => {
      state.rosters[action.payload.team].currentLineup.splice(
        action.payload.index,
        1,
        action.payload.newPlayer,
      );
    },
    makeCatcherSub: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: { payload: { team: TTeamSides; player: any } },
    ) => {
      state.rosters[action.payload.team].currentCatcher = action.payload.player;
    },
    incrementPitchCount: (state, action: { payload: { team: TTeamSides } }) => {
      state.rosters[action.payload.team].pitchCount =
        state.rosters[action.payload.team].pitchCount + 1;
    },
    resetPitchCount: (state, action: { payload: { team: TTeamSides } }) => {
      state.rosters[action.payload.team].pitchCount = 0;
    },
    addToRoster: (
      state,
      action: { payload: { player: IPlayer; team: TTeamSides } },
    ) => {
      state.rosters[action.payload.team].roster.push(action.payload.player);
    },
    addPlayerToRoster: (
      state,
      action: { payload: { player: IPlayer; team: TTeamSides } },
    ) => {
      state.rosters[action.payload.team].roster.push(action.payload.player);
    },
    removeFromRoster: (
      state,
      action: { payload: { team: TTeamSides; index: number } },
    ) => {
      state.rosters[action.payload.team].roster.splice(action.payload.index, 1);
    },
    addToLineup: (
      state,
      action: { payload: { team: TTeamSides; player: IPlayer } },
    ) => {
      state.rosters[action.payload.team].currentLineup.push(
        action.payload.player,
      );
    },
    removeFromLineup: (
      state,
      action: { payload: { team: TTeamSides; player: IPlayer; uuid: string } },
    ) => {
      state.rosters[action.payload.team].currentLineup = state.rosters[
        action.payload.team
      ].currentLineup.filter((player) => player.uuid !== action.payload.uuid);
    },
    ascendLineup: (
      state,
      action: { payload: { team: TTeamSides; uuid: string } },
    ) => {
      const prevIndex = state.rosters[
        action.payload.team
      ].currentLineup.findIndex(
        (player) => player.uuid === action.payload.uuid,
      );
      const objectToMove =
        state.rosters[action.payload.team].currentLineup[prevIndex];
      state.rosters[action.payload.team].currentLineup.splice(prevIndex, 1);
      state.rosters[action.payload.team].currentLineup.splice(
        prevIndex - 1,
        0,
        objectToMove,
      );
    },
    descendLineup: (
      state,
      action: { payload: { team: TTeamSides; uuid: string } },
    ) => {
      const prevIndex = state.rosters[
        action.payload.team
      ].currentLineup.findIndex(
        (player) => player.uuid === action.payload.uuid,
      );
      const objectToMove =
        state.rosters[action.payload.team].currentLineup[prevIndex];
      state.rosters[action.payload.team].currentLineup.splice(prevIndex, 1);
      state.rosters[action.payload.team].currentLineup.splice(
        prevIndex + 1,
        0,
        objectToMove,
      );
    },
    reorderLineup: (
      state,
      action: { payload: { team: TTeamSides; lineup: Array<IPlayer> } },
    ) => {
      state.rosters[action.payload.team].currentLineup = action.payload.lineup;
    },
    setCurrentPitcher: (
      state,
      action: { payload: { team: TTeamSides; player: IPlayer } },
    ) => {
      state.rosters[action.payload.team].currentPitcher = action.payload.player;
    },
    clearCurrentPitcher: (state, action: { payload: { team: TTeamSides } }) => {
      state.rosters[action.payload.team].currentPitcher = {} as IPlayer;
    },
    setCurrentCatcher: (
      state,
      action: { payload: { team: TTeamSides; player: IPlayer } },
    ) => {
      state.rosters[action.payload.team].currentCatcher = action.payload.player;
    },
    clearCurrentCatcher: (state, action: { payload: { team: TTeamSides } }) => {
      state.rosters[action.payload.team].currentCatcher = {} as IPlayer;
    },
    setCurrentHitter: (
      state,
      action: { payload: { team: TTeamSides; player: CurrentHitter } },
    ) => {
      state.rosters[action.payload.team].currentHitter = action.payload.player;
    },
    setCurrentLineup: (
      state,
      action: { payload: { team: TTeamSides; lineup: Array<IPlayer> } },
    ) => {
      state.rosters[action.payload.team].currentLineup = action.payload.lineup;
    },
  },
});

export const {
  reorderLineup,
  addPlayerToRoster,
  clearCurrentCatcher,
  clearCurrentPitcher,
  clearRoster,
  endGame,
  setGameActive,
  setGameState,
  setAreRostersOpen,
  setCurrentHitter,
  setCurrentPitcher,
  setCurrentCatcher,
  ascendLineup,
  descendLineup,
  addToLineup,
  removeFromLineup,
  addToRoster,
  removeFromRoster,
  makeCatcherSub,
  makeOffensiveSub,
  setTeam,
  setRoster,
  resetPitchCount,
  incrementPitchCount,
  setCurrentLineup,
} = gameSlice.actions;
export default gameSlice.reducer;
